import { useAuthentication } from '@givebacks/api';
import { UserCartActionIcon } from '@givebacks/shop';
import { useBreakPoints, useManageBackButton, usePlatformService } from 'core/Hooks';
import { NavLink } from 'react-router-dom';
import { Anchor, AppShell, Box, Center, Group } from 'ui';
import { LogoGBSmallBlue, LogoGivebacksBlue } from 'ui/Icons';

import { HamburgerMenu } from './Components/CartMenuActions';
import { GetStartedActions } from './Components/GetStartedActions';
import { DesktopNavLinks } from './Components/Menus/DesktopMenu/DesktopNavLinks';
import classes from './MainHeader.module.css';

const appShellHeaderId = 'appshell-header';

const RightHeaderActions = () => {
  return (
    <Group gap={'xs'}>
      <GetStartedActions />
      <UserCartActionIcon />
      <HamburgerMenu />
    </Group>
  );
};

// Creates a wrapper around the mobile logos, don't show the anchor tag if it's a native app
const MobileHeaderLogo = ({ children }: { children: React.ReactNode }) => {
  return (
    <Anchor component={NavLink} to="/home" m="auto">
      {children}
    </Anchor>
  );
};

const MobileHeader = () => {
  const { isAuthenticated } = useAuthentication();

  const { ref, isLogoHidden } = useManageBackButton();
  const { isSmallScreen } = useBreakPoints();

  return (
    <Group
      style={{ width: '100%' }}
      justify="space-between"
      wrap="nowrap"
      mih={'45px'}
      hiddenFrom="sm"
    >
      {/* Id is used to give to the observer, a ref doesn't exist on the first render pass
        so we need to use the id to get the element for the observer
      */}
      <Center ref={ref} id="header-back-button-container">
        <Box hidden={isLogoHidden}>
          <MobileHeaderLogo>
            {isAuthenticated ? <LogoGivebacksBlue /> : <LogoGBSmallBlue />}
          </MobileHeaderLogo>
        </Box>
      </Center>
      {isSmallScreen && <RightHeaderActions />}
    </Group>
  );
};

const DesktopHeader = () => {
  const { isMediumScreen } = useBreakPoints();

  return (
    // This matches current xl container size but should be updated later to match design
    <Group w="100%" maw={1550} m="auto" justify="space-between" wrap="nowrap" visibleFrom="sm">
      <Anchor component={NavLink} to="/home">
        {isMediumScreen ? (
          <LogoGBSmallBlue h="30px" w="auto" />
        ) : (
          <LogoGivebacksBlue h="30px" w="auto" />
        )}
      </Anchor>
      <DesktopNavLinks />
      <RightHeaderActions />
    </Group>
  );
};

const MainHeader = () => {
  const Platform = usePlatformService();
  const { isSmallScreen } = useBreakPoints();

  return (
    <AppShell.Header
      px={{ base: 'lg', sm: 'xl' }}
      mod={{
        'is-native': Platform.isNative,
      }}
      id={appShellHeaderId}
      className={classes.header}
      withBorder={!isSmallScreen}
    >
      <MobileHeader />
      <DesktopHeader />
    </AppShell.Header>
  );
};

export { MainHeader };
